import React, { createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { usePlanUsage } from "../hooks/useAdminShell";
import { Lock, ArrowUpRight } from "lucide-react";

const PlanContext = createContext({ 
  features: {}, 
  trialFeatures: {},
  planName: "Free Trial", 
  planSlug: "free_trial", 
  loading: true 
});

export function PlanProvider({ children }) {
  // Wave 3 C5: plan usage comes from the cached admin-shell bundle (shared with the layout).
  const { data, isLoading } = usePlanUsage();
  const planData = React.useMemo(() => ({
    features: data?.features || {},
    trialFeatures: data?.trial_features || {},
    planName: data?.plan_name || "Free Trial",
    planSlug: data?.plan_slug || "free_trial",
    loading: isLoading,
  }), [data, isLoading]);

  return <PlanContext.Provider value={planData}>{children}</PlanContext.Provider>;
}

export function usePlan() {
  return useContext(PlanContext);
}

export function PlanGate({ feature, children, fallback }) {
  const { features, planName, loading } = usePlan();
  const navigate = useNavigate();

  if (loading) return children;

  const unlocked = features[feature] !== false;
  if (unlocked) return children;

  if (fallback) return fallback;

  return (
    <div className="relative group" data-testid={`plan-gate-${feature}`}>
      <div className="pointer-events-none opacity-40 blur-[1px] select-none">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[2px] rounded-xl">
        <div className="text-center px-4 pointer-events-auto">
          <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-2">
            <Lock className="h-5 w-5 text-slate-400" />
          </div>
          <p className="text-xs font-bold text-slate-700">Upgrade to Unlock</p>
          <p className="text-[10px] text-slate-500 mt-0.5">This feature requires a higher plan</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Current: {planName}</p>
          <button
            onClick={() => navigate(`/admin/upgrade?feature=${feature}`)}
            className="mt-2 text-[11px] font-bold text-sky-600 hover:text-sky-700 underline underline-offset-2 transition-colors"
            data-testid={`plan-gate-upgrade-${feature}`}
          >
            View Plans
          </button>
        </div>
      </div>
    </div>
  );
}

export function PlanGateInline({ feature, children }) {
  const { features, loading } = usePlan();
  if (loading) return children;
  const unlocked = features[feature] !== false;
  if (unlocked) return children;

  return (
    <div className="flex items-center gap-1.5 opacity-50 cursor-not-allowed" data-testid={`plan-gate-inline-${feature}`}>
      {children}
      <Lock className="h-3 w-3 text-slate-400" />
    </div>
  );
}

export function PlanBadge({ feature }) {
  const { features, loading } = usePlan();
  if (loading) return null;
  if (features[feature] !== false) return null;
  return (
    <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
      <ArrowUpRight className="h-2.5 w-2.5" /> PRO
    </span>
  );
}
